import React, { useState, useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import mpegts from 'mpegts.js';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Settings,
  SkipBack,
  SkipForward,
  Loader2,
  ArrowLeft,
  RefreshCw,
  Tv
} from 'lucide-react';

const VideoPlayer = ({ src, poster, title, onBack, isLive = false, onProgress, initialTime = 0 }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const progressRef = useRef(null);
  const hideControlsTimer = useRef(null);
  const progressSaveTimer = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [quality, setQuality] = useState(-1);
  const [qualities, setQualities] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const cleanupPlayer = useCallback(() => {
    if (playerRef.current) {
      if (playerRef.current.destroy) {
        playerRef.current.destroy();
      }
      playerRef.current = null;
    }
  }, []);

  const initPlayer = useCallback(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    setError(null);
    setIsBuffering(true);

    // Cleanup previous player
    cleanupPlayer();

    // Determine stream type
    const isHls = src.includes('.m3u8');
    const isMpegTs = src.includes('.ts');
    const isMkv = src.includes('.mkv');

    if (isHls && Hls.isSupported()) {
      // HLS stream
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: isLive ? 0 : 30,
        maxBufferLength: isLive ? 30 : 30,
        maxMaxBufferLength: isLive ? 60 : 600,
        maxBufferHole: 0.5,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
        liveDurationInfinity: isLive,
        progressive: true,
      });

      playerRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        const levels = data.levels.map((level, index) => ({
          index,
          height: level.height,
          bitrate: level.bitrate,
          label: level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)}kbps`
        }));
        setQualities([{ index: -1, label: 'Auto' }, ...levels]);
        setIsBuffering(false);
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS Error:', data);
        if (data.fatal) {
          if (retryCount < 3) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
              hls.startLoad();
            }, 2000);
          } else {
            setError('Failed to load HLS stream');
          }
        }
      });
    } else if (isMpegTs && mpegts.isSupported()) {
      // MPEG-TS stream using mpegts.js (fallback for .ts URLs)
      const player = mpegts.createPlayer({
        type: 'mpegts',
        isLive: isLive,
        url: src,
      }, {
        enableWorker: true,
        lazyLoad: false,
        autoCleanupSourceBuffer: true,
        autoCleanupMaxBackwardDuration: 30,
        autoCleanupMinBackwardDuration: 15,
        stashInitialSize: 512 * 1024,
        enableStashBuffer: true,
        liveBufferLatencyChasing: false,
        liveBufferLatencyMaxLatency: 10,
        liveBufferLatencyMinRemain: 3,
      });

      playerRef.current = player;
      player.attachMediaElement(video);
      player.load();

      player.on(mpegts.Events.ERROR, (errorType, errorDetail, errorInfo) => {
        console.error('mpegts.js error:', errorType, errorDetail, errorInfo);
        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            player.unload();
            player.load();
            player.play();
          }, 2000);
        } else {
          setError(`Stream error: ${errorDetail || 'Unknown error'}`);
        }
      });

      player.on(mpegts.Events.LOADING_COMPLETE, () => {
        setIsBuffering(false);
      });

      player.on(mpegts.Events.MEDIA_INFO, () => {
        setIsBuffering(false);
        video.play().catch(() => {});
      });

      // Start playback
      player.play();
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS (Safari)
      video.src = src;
      video.load();
      video.addEventListener('loadedmetadata', () => {
        setIsBuffering(false);
        video.play().catch(() => {});
      });
    } else {
      // Try direct playback (for mkv, etc.)
      video.src = src;
      video.crossOrigin = 'anonymous';
      
      video.onloadeddata = () => {
        setIsBuffering(false);
        video.play().catch(() => {});
      };
      
      video.onerror = () => {
        setError('This video format may not be supported by your browser. Try a different stream.');
      };
      
      video.load();
    }
  }, [src, isLive, retryCount, cleanupPlayer]);

  useEffect(() => {
    initPlayer();
    return cleanupPlayer;
  }, [initPlayer, cleanupPlayer]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleDurationChange = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleWaiting = () => setIsBuffering(true);
    const handlePlaying = () => setIsBuffering(false);
    const handleCanPlay = () => setIsBuffering(false);
    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('volumechange', handleVolumeChange);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('volumechange', handleVolumeChange);
    };
  }, []);

  // Save progress periodically and on pause/unmount
  useEffect(() => {
    if (isLive || !onProgress) return;
    
    progressSaveTimer.current = setInterval(() => {
      const video = videoRef.current;
      if (video && video.currentTime > 0 && video.duration > 0) {
        onProgress(video.currentTime, video.duration);
      }
    }, 10000); // Save every 10 seconds

    return () => {
      clearInterval(progressSaveTimer.current);
      // Save on unmount
      const video = videoRef.current;
      if (video && video.currentTime > 0 && video.duration > 0) {
        onProgress(video.currentTime, video.duration);
      }
    };
  }, [isLive, onProgress]);

  // Save on pause
  useEffect(() => {
    if (isLive || !onProgress || isPlaying) return;
    const video = videoRef.current;
    if (video && video.currentTime > 0 && video.duration > 0) {
      onProgress(video.currentTime, video.duration);
    }
  }, [isPlaying, isLive, onProgress]);

  // Seek to initial time when video is ready
  useEffect(() => {
    if (!initialTime || isLive) return;
    const video = videoRef.current;
    if (video && video.readyState >= 1) {
      video.currentTime = initialTime;
    } else if (video) {
      const seekOnReady = () => {
        video.currentTime = initialTime;
        video.removeEventListener('loadedmetadata', seekOnReady);
      };
      video.addEventListener('loadedmetadata', seekOnReady);
      return () => video.removeEventListener('loadedmetadata', seekOnReady);
    }
  }, [initialTime, isLive, src]);

  // Hide controls after inactivity
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(hideControlsTimer.current);
      hideControlsTimer.current = setTimeout(() => {
        if (isPlaying) setShowControls(false);
      }, 3000);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('touchstart', handleMouseMove);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('touchstart', handleMouseMove);
      }
      clearTimeout(hideControlsTimer.current);
    };
  }, [isPlaying]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
    }
  }, [isPlaying]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
    }
  };

  const skip = useCallback((seconds) => {
    const video = videoRef.current;
    if (video && !isLive) {
      video.currentTime = Math.max(0, Math.min(duration, video.currentTime + seconds));
    }
  }, [duration, isLive]);

  const handleProgressClick = (e) => {
    if (isLive) return;
    const video = videoRef.current;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (video) {
      video.currentTime = percent * duration;
    }
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!document.fullscreenElement) {
      container.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const setQualityLevel = (levelIndex) => {
    if (playerRef.current && playerRef.current.currentLevel !== undefined) {
      playerRef.current.currentLevel = levelIndex;
      setQuality(levelIndex);
    }
    setShowSettings(false);
  };

  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    initPlayer();
  };

  const formatTime = (time) => {
    if (isNaN(time) || !isFinite(time)) return '0:00';
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'm':
          toggleMute();
          break;
        case 'ArrowLeft':
          skip(-10);
          break;
        case 'ArrowRight':
          skip(10);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, skip]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (error) {
    return (
      <div data-testid="video-player-error" className="relative w-full h-full bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Tv className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-white text-lg font-semibold mb-2">Stream Unavailable</h3>
          <p className="text-gray-400 text-sm mb-4">
            This stream could not be loaded. This may happen if the channel is currently offline, 
            the content is not available in your region, or there is a limitation with your subscription account.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              data-testid="video-retry-btn"
              onClick={handleRetry}
              className="px-5 py-2 rounded-lg text-white text-sm hover:opacity-90 transition-colors flex items-center gap-2"
              style={{backgroundColor:"var(--accent)"}}
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
            {onBack && (
              <button
                data-testid="video-back-btn"
                onClick={onBack}
                className="px-5 py-2 bg-white/10 rounded-lg text-white text-sm hover:bg-white/20 transition-colors"
              >
                Go Back
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full bg-black group"
      onClick={togglePlay}
    >
      <video
        ref={videoRef}
        className="w-full h-full"
        poster={poster}
        playsInline
      />

      {/* Buffering Indicator */}
      {isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
          <Loader2 className="w-16 h-16 text-white animate-spin" />
        </div>
      )}

      {/* Controls Overlay */}
      <div
        className={`absolute inset-0 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Gradient */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/70 to-transparent" />

        {/* Title Bar */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-lg font-medium">{title}</span>
          </button>
        </div>

        {/* Center Play/Pause */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <button
            onClick={togglePlay}
            className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors pointer-events-auto"
          >
            {isPlaying ? (
              <Pause className="w-10 h-10 text-white" />
            ) : (
              <Play className="w-10 h-10 text-white ml-1" />
            )}
          </button>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pt-16 pb-4 px-4">
          {/* Progress Bar (hide for live) */}
          {!isLive && (
            <div
              ref={progressRef}
              className="h-1 bg-white/30 rounded-full cursor-pointer mb-4 group/progress"
              onClick={handleProgressClick}
            >
              <div
                className="h-full rounded-full relative"
                style={{ width: `${progress}%`, backgroundColor: 'var(--accent)' }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity" />
              </div>
            </div>
          )}

          {/* Controls Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={togglePlay} className="text-white hover:text-gray-300">
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>

              {!isLive && (
                <>
                  <button onClick={() => skip(-10)} className="text-white hover:text-gray-300">
                    <SkipBack className="w-5 h-5" />
                  </button>
                  <button onClick={() => skip(10)} className="text-white hover:text-gray-300">
                    <SkipForward className="w-5 h-5" />
                  </button>
                </>
              )}

              <div className="flex items-center gap-2">
                <button onClick={toggleMute} className="text-white hover:text-gray-300">
                  {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    const video = videoRef.current;
                    if (video) {
                      video.volume = parseFloat(e.target.value);
                      video.muted = false;
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-20 h-1 accent-purple-500 cursor-pointer"
                />
              </div>

              {!isLive && (
                <span className="text-white text-sm">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              )}
            </div>

            <div className="flex items-center gap-4">
              {/* Quality Settings */}
              {qualities.length > 1 && (
                <div className="relative">
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="text-white hover:text-gray-300"
                  >
                    <Settings className="w-5 h-5" />
                  </button>

                  {showSettings && (
                    <div className="absolute bottom-full right-0 mb-2 bg-black/90 rounded-lg overflow-hidden min-w-[120px]">
                      <div className="p-2 text-xs text-gray-400 border-b border-gray-700">Quality</div>
                      {qualities.map((q) => (
                        <button
                          key={q.index}
                          onClick={() => setQualityLevel(q.index)}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                            quality === q.index ? 'text-white' : 'text-gray-300'
                          }`}
                        >
                          {q.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button onClick={toggleFullscreen} className="text-white hover:text-gray-300">
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
